// @ts-strict-ignore
import type { MouseEvent } from 'react';
import React, {
  Fragment,
  useCallback,
  useMemo,
  useState,
  memo,
  useEffect,
} from 'react';
import { Tooltip } from '@usealloy/component-library';

import { ActionType } from 'flow/store/engine/constants';
import {
  beginLinkedChildActionThunk,
  exitLinkedChildActionThunk,
} from 'flow/store/redux/thunks';
import {
  createThreshold,
  removeThreshold,
  setConnectorInteractionState,
  setConnectorOwnerNodeId,
  updateLinkedChildActionThresholdIds,
  createDataRoute,
  updateThreshold,
  removeDataRoute,
  updateDependencies,
} from 'flow/store/redux/slice';
import { generatePublicId } from 'flow/service/publicIds';
import {
  isActionAnImportedValue,
  isActionOfType,
  isDefaultPrecedenceIndexedAction,
  isPrecedenceIndexedAction,
} from 'flow/service/actions';
import {
  selectFocusedAction,
  selectFolderNode,
  selectIsEditingLinkedChildAction,
  selectIsPendingHistoryCommit,
  selectNodeConnectorState,
  selectNodeLineageForAnchorNode,
  selectPreviewAction,
  selectSnippetMode,
} from 'flow/store/redux/selectors';
import { useDelayedState, useAnimationTrigger } from 'flow/editor/hooks';
import { isFolderNodeCollapsed, isFolderNodeId } from 'flow/service/graph';
import type { WorkflowNode } from 'flow/service/graph';

import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';

import {
  canLinkToNode,
  getConnectorIcon,
  getConnectorTooltipContent,
} from './helpers';
import {
  DotContainer,
  DotIcon,
  Dot,
} from './styles';
import { isSnippetChildAction } from 'flow/service/actions/snippets';


interface NodeConnectorProps {
  connectorParentNodeId: WorkflowNode['id'];
  onMouseEnter?: React.MouseEventHandler;
  onMouseLeave?: React.MouseEventHandler;
}

function NodeConnector({
  connectorParentNodeId,
  onMouseEnter,
  onMouseLeave,
}: NodeConnectorProps) {
  const dispatch = useAppDispatch();

  const anchorNodeLineage = useAppSelector(selectNodeLineageForAnchorNode);
  const isEditingLinkedChildAction = useAppSelector(selectIsEditingLinkedChildAction);
  const previewActionBeingBuilt = useAppSelector(selectPreviewAction);
  const focusedAction = useAppSelector(selectFocusedAction);
  const folderNode = useAppSelector(selectFolderNode);
  const snippetModeState = useAppSelector(selectSnippetMode);
  const isPendingHistoryCommit = useAppSelector(selectIsPendingHistoryCommit);

  const {
    intent: globalIntent,
    interactionState,
    canCreateItem,
  } = useAppSelector(selectNodeConnectorState);

  const [isVisible] = useAnimationTrigger();

  /**
   * State of the slide in & slide out animation.
   * idle = hidden. entering = sliding in. entered = expanded. exiting = sliding out
   * These values are idiomatic in animation libraries.
   */
  const [animationState, setAnimationState] = useState<'idle' | 'entering' | 'entered' | 'exiting'>('idle');

  /**
   * The node connector global intent goes from "add" to "link" as soon as the user mouse-leaves a
   * focused node. That is the expected behavior, but the component enters and exists the viewport
   * through a CSS transition animation. That creates an invalid intermediary state for the connector,
   * where it has a "link" intent during its hiding (exiting) animation state. That short-lived
   * invalid intent can create self-referencing thresholds, as the node tries to link to itself if
   * the user hovers on an exiting connector. To avoid this, we override the "link" intent during
   * the exit animation state.
   */
  let intent = globalIntent;
  if (animationState === 'exiting' && intent === 'link') {
    intent = 'add';
  }

  const [thresholdId, setThresholdId] = useState<string>(null);
  const [dataRouteId, setDataRouteId] = useState<string>(null);

  // For collapsed folder nodes, node connector creates actions associated with its first child node
  const creationNodeId = (
    isFolderNodeId(connectorParentNodeId) && isFolderNodeCollapsed(folderNode)
  ) ? folderNode.data.children[0].id
    : connectorParentNodeId;

  const cannotLinkToNode = !canLinkToNode(anchorNodeLineage, connectorParentNodeId);
  const cannotAddAsThreshold = isSnippetChildAction(focusedAction) && intent === 'link';

  const isInputOrImportedValueWithLinkIntent = (isActionOfType(focusedAction, ActionType.INPUT)
    || isActionAnImportedValue(focusedAction))
    && intent === 'link';

  const isDefaultPrecedenceIndexed = isPrecedenceIndexedAction(focusedAction)
    && isDefaultPrecedenceIndexedAction(focusedAction);

  const isDisabled = cannotLinkToNode
    || cannotAddAsThreshold
    || isInputOrImportedValueWithLinkIntent
    || isDefaultPrecedenceIndexed
    || Boolean(snippetModeState)
    || (isPendingHistoryCommit && intent === 'add');

  const connectorIcon = getConnectorIcon(intent);
  const connectorTooltip = getConnectorTooltipContent(intent, focusedAction, isDisabled);

  const {
    reset: closeTooltip, value: isTooltipOpen,
  } = useDelayedState<boolean>({
    initialState: false,
    valueSetDelay: 1_000,
  });

  useEffect(() => {
    const animationTransitionState = isVisible ? 'entering' : 'exiting';
    setAnimationState(animationTransitionState);
  }, [isVisible]);


  const clearPreview = useCallback(() => {
    if (thresholdId) {
      dispatch(removeThreshold({ id: thresholdId }));
      setThresholdId(null);
    }

    if (dataRouteId) {
      dispatch(removeDataRoute({ id: dataRouteId }));
      setDataRouteId(null);
    }
  }, [dispatch, thresholdId, dataRouteId]);

  useEffect(() => {
    if (!isVisible) {
      clearPreview();
    }
  }, [clearPreview, isVisible]);

  const handleMouseEnter = useCallback((e: React.MouseEvent) => {
    // TODO: debug this so tooltip shows up but doesn't trigger multiple re-renders
    // openTooltip();
    if (isDisabled) return;
    dispatch(setConnectorInteractionState('hovered'));
    dispatch(setConnectorOwnerNodeId(connectorParentNodeId));

    if (intent === 'link') {
      if (!canCreateItem) return;
      if (isActionOfType(focusedAction, ActionType.OUTPUT)) {
        const newDataRouteId = generatePublicId('dataroute');
        setDataRouteId(newDataRouteId);
        dispatch(createDataRoute({
          path: newDataRouteId,
          value: {
            publicId: newDataRouteId,
            sourceId: creationNodeId,
            targetId: focusedAction.publicId,
          },
          expressionPath: '-1', // this is a special path to make the logic not append in front of the expression.
          isPreview: true,
        }));
      } else {
        const newThresholdId = generatePublicId('threshold');
        setThresholdId(newThresholdId);
        dispatch(createThreshold({
          isPreview: true,
          path: `${newThresholdId}.left.actionId`,
          value: {
            leftActionId: creationNodeId,
          },
        }));
      }
    }

    onMouseEnter?.(e);
  }, [
    canCreateItem,
    connectorParentNodeId,
    creationNodeId,
    dispatch,
    focusedAction,
    intent,
    isDisabled,
    onMouseEnter,
  ]);

  const handleMouseLeave = useCallback((e: React.MouseEvent) => {
    if (isDisabled) return;

    closeTooltip();
    clearPreview();

    if (interactionState !== 'focused') {
      dispatch(setConnectorInteractionState(null));
    }

    onMouseLeave?.(e);
  }, [
    clearPreview, closeTooltip, dispatch,
    interactionState, onMouseLeave, isDisabled,
  ]);


  const handleOnClick = useCallback((e: MouseEvent) => {
    e.stopPropagation();
    if (intent === 'add') {
      if (isEditingLinkedChildAction) {
        dispatch(exitLinkedChildActionThunk(previewActionBeingBuilt.publicId));
      }
      dispatch(beginLinkedChildActionThunk(creationNodeId));
    }

    if (intent === 'link') {
      if (!canCreateItem) return;

      if (isActionOfType(focusedAction, ActionType.OUTPUT)) {
        // output expression mode
        const isAddingPreviewDataRoute = dataRouteId !== null;
        if (isAddingPreviewDataRoute) {
          dispatch(updateDependencies()); // data route is already there, we just need to update dependency map
          setDataRouteId(null);
        } else {
          const newDataRouteId = generatePublicId('dataroute');
          dispatch(createDataRoute({
            path: newDataRouteId,
            value: {
              publicId: newDataRouteId,
              sourceId: creationNodeId,
              targetId: focusedAction.publicId,
            },
            expressionPath: '-1', // this is a special path to make the logic not append in front of the expression.
          }));
        }
      } else {
        const isAddingPreviewThreshold = thresholdId !== null;
        if (isAddingPreviewThreshold) {
          dispatch(updateThreshold({
            path: `${thresholdId}.isPreview`,
            value: false,
          }));

          if (isEditingLinkedChildAction) {
            dispatch(updateLinkedChildActionThresholdIds({ operation: 'add', thresholdId }));
          }

          /**
           * Have to unset this here otherwise the `handleMouseLeave` handler will delete
           * the new threshold by the `thresholdId` right away, even though the user
           * clicked to create it from the preview shown
          */
          setThresholdId(null);
        } else {
          const newThresholdId = generatePublicId('threshold');
          dispatch(createThreshold({
            path: `${newThresholdId}.left.actionId`,
            value: {
              leftActionId: creationNodeId,
            },
          }));

          if (isEditingLinkedChildAction) {
            dispatch(updateLinkedChildActionThresholdIds({ operation: 'add', thresholdId: newThresholdId }));
          }
        }
      }
    }
  }, [
    creationNodeId,
    dispatch,
    dataRouteId,
    intent,
    isEditingLinkedChildAction,
    previewActionBeingBuilt?.publicId,
    thresholdId,
    focusedAction,
    canCreateItem,
  ]);


  /**
   * this callback runs when the CSS transition ends on either direction. we look at which
   * transition state we're in to determine if the animation ended on connector hidden or expanded
   */
  const handleOnTransitionEnd = useCallback(() => {
    // these are not logically possible, but we still defend against them
    if (animationState === 'idle' || animationState === 'entered') {
      return;
    }
    const animationEndState = animationState === 'entering' ? 'entered' : 'idle';
    setAnimationState(animationEndState);
  }, [animationState]);

  // only wrap the Connector in a Tooltip if we need to show one
  const Wrapper = useMemo(() => (
    connectorTooltip
      ? ({ children }) => (
        <Tooltip
          content={connectorTooltip}
          open={isTooltipOpen}
          style={{ whiteSpace: 'pre-line' }}
        >{children}
        </Tooltip>
      )
      : Fragment
  ), [connectorTooltip, isTooltipOpen]);

  const dataTestId = `workflowNodeConnector-${connectorParentNodeId}`;

  return canCreateItem || (!canCreateItem && intent === 'add') ? (
    <DotContainer
      isDisabled={isDisabled}
      isVisible={isVisible}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTransitionEnd={handleOnTransitionEnd}
      {...(!isDisabled ? {
        onClick: handleOnClick,
        onDoubleClick: e => e.stopPropagation(),
      } : {
        onClick: e => e.stopPropagation(),
      })}
      data-testid={dataTestId}
      data-value={intent}
      data-dd-action-name={`workflow-node-connector-${intent}`}
    >
      <Wrapper>
        <Dot isDisabled={isDisabled}>
          <DotIcon icon={connectorIcon} size="small" isDisabled={isDisabled} />
        </Dot>
      </Wrapper>
    </DotContainer>
  ) : null;
}

export default memo(NodeConnector);
